export const NEW_LISTING_WINDOW_DAYS = 30;
const NEW_LISTING_WINDOW_MS = NEW_LISTING_WINDOW_DAYS * 24 * 60 * 60 * 1000;

const dateToTimestamp = value => {
  if (value == null || value === '') {
    return null;
  }

  const timestamp =
    value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);

  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Date used by the existing "New Listing" card badge.
 *
 * SceneAccess does not currently persist a separate label flag. The existing
 * badge is derived from Sharetribe's immutable listing creation timestamp, so
 * edits and re-publication do not make an old listing new again.
 */
export const getNewListingReferenceDate = listing => listing?.attributes?.createdAt || null;

export const isNewListing = (listingDate, referenceDate = new Date()) => {
  const listingTimestamp = dateToTimestamp(listingDate);
  const referenceTimestamp = dateToTimestamp(referenceDate);

  if (!Number.isFinite(listingTimestamp) || !Number.isFinite(referenceTimestamp)) {
    return false;
  }

  const listingAgeMs = referenceTimestamp - listingTimestamp;
  return listingAgeMs >= 0 && listingAgeMs <= NEW_LISTING_WINDOW_MS;
};

export const isListingNew = (listing, referenceDate = new Date()) =>
  isNewListing(getNewListingReferenceDate(listing), referenceDate);

const isPubliclyVisibleListing = listing => {
  const attributes = listing?.attributes || {};
  return attributes.state === 'published' && attributes.deleted !== true;
};

/**
 * Landing-page eligibility uses the exact same rule as the listing-card badge,
 * then orders eligible public listings by createdAt, newest first.
 */
export const getNewestPublishedListings = (listings, referenceDate = new Date()) => {
  if (!Array.isArray(listings)) {
    return [];
  }

  const seenListingIds = new Set();

  return listings
    .map((listing, index) => ({
      listing,
      index,
      createdAtTimestamp: dateToTimestamp(getNewListingReferenceDate(listing)),
    }))
    .filter(({ listing }) => {
      const listingId = listing?.id?.uuid;
      if (!listingId || seenListingIds.has(listingId)) {
        return false;
      }

      const isEligible = isPubliclyVisibleListing(listing) && isListingNew(listing, referenceDate);
      if (isEligible) {
        seenListingIds.add(listingId);
      }
      return isEligible;
    })
    .sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp || a.index - b.index)
    .map(({ listing }) => listing);
};
